import {Props_KeyFigures} from './KeyFigures.props'
import {CompileMarkdown} from "../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler";
import {useState, useEffect} from 'react';
import {motion, useInView, useMotionValue, useTransform, animate} from 'framer-motion';
import {useRef} from 'react';

interface AnimatedCounterProps {
    value: string;
    className: string;
}

const AnimatedCounter = ({value, className}: AnimatedCounterProps) => {
    const ref = useRef<HTMLSpanElement>(null);
    const isInView = useInView(ref, {once: true, margin: "-100px"});
    
    // Remove commas and parse the number
    const numericValue = parseFloat(value.replace(/,/g, ''));
    
    // Check if the value is a valid number
    if (isNaN(numericValue)) {
        return <span className={className}>{value}</span>;
    }
    
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => {
        // Format the number with commas if the original had commas
        const hasCommas = value.includes(',');
        const roundedValue = Math.round(latest);
        
        if (hasCommas) {
            return roundedValue.toLocaleString();
        }
        return roundedValue.toString();
    });
    
    useEffect(() => {
        if (isInView) {
            const controls = animate(count, numericValue, {
                duration: 3,
                ease: "easeOut"
            });
            
            return controls.stop;
        }
    }, [isInView, numericValue, count]);
    
    return <motion.span ref={ref} className={className}>{rounded}</motion.span>;
};

const SnippetKeyFigures = (props: Props_KeyFigures) => {
    const {heading, repeatable_card} = props;
    const baseClass = 'snippet-key-figures';
    const [bgImage, setBgImage] = useState('/Blob/images/pattern-bg-lg-rv.png');

    useEffect(() => {
        const updateBgImage = () => {
            if (window.innerWidth > 990) {
                setBgImage('/Blob/images/pattern-bg-lg.png');
            } else {
                setBgImage('/Blob/images/pattern-bg-lg-rv.png');
            }
        };
        
        updateBgImage();
        
        window.addEventListener('resize', updateBgImage);
        
        return () => window.removeEventListener('resize', updateBgImage);
    }, []);

    return (
        <section className={`${baseClass}`}>
            <img className={`${baseClass}__bg-img`} src={bgImage} alt="" />
            <div className={`${baseClass}__container`}>
                {heading?.content?.value && 
                    <h2 className={`${baseClass}__heading ${heading.visibility?.value === false ? 'accessible-heading' : ''}`}>
                        {heading.content.value}
                    </h2>
                }
                
                
                <div className={`${baseClass}__cards-container`}>
                    {repeatable_card?.map((card, index) => (
                        <div className={`${baseClass}__card`} key={index}>
                            {card.icon?.value &&
                                <div className={`${baseClass}__card-icon`}>
                                    <img src={card.icon.value} alt='' />
                                </div>
                            }
                            <div className={`${baseClass}__card-stat
                                ${card.decoratorPosition?.value === 'after' ? `${baseClass}__card-stat--decorator-after` : ''}
                            `}>
                                {card.statDecorator?.value &&
                                    <span className={`${baseClass}__stat-decorator`}>
                                        {card.statDecorator.value}
                                    </span>
                                }
                                
                                {card.stat?.value && (
                                    <AnimatedCounter 
                                        value={card.stat.value} 
                                        className={`${baseClass}__stat-value`}
                                    />
                                )}
                            </div>
                            {card.subHeading?.value &&
                                <h3 className={`${baseClass}__card-subheading`}>
                                    {card.subHeading.value}
                                </h3>
                            }
                            {card.content?.value &&
                                <div className={`${baseClass}__card-content`}>
                                    {CompileMarkdown(card.content.value)}
                                </div>
                            }
                        </div>
                    ))}
                </div>
            </div>
        </section>
        
    )
}

export default SnippetKeyFigures;